import jsPDF from 'jspdf';

export async function generatePDFReport(
  chartImageBase64: string,
  overallRating: number,
  strengths: Array<{ dimension: string | string[]; score: number; avg: number }>,
  improvements: Array<{ dimension: string | string[]; score: number; avg: number }>,
  returnBase64: boolean = false
) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const borderMargin = 14; // space from page edge to border
  const contentMargin = borderMargin + 10; // space from border to content
  const borderRadius = 8;
  const borderColor = '#d1d5db'; // light grey
  const bgColor = '#f8f9fa';
  const logoWidth = 2 * 28.35; // 2cm in points
  let logoHeight = 0;
  let logoLoaded = false;

  // Add small logo (2cm wide) to the top right OUTSIDE the border
  try {
    const logoImg = new Image();
    logoImg.src = '/images/DJLogo2025_small.jpg';
    await new Promise((resolve) => { logoImg.onload = resolve; });
    logoHeight = (logoImg.height * logoWidth) / logoImg.width;
    doc.addImage(
      logoImg,
      'JPEG',
      pageWidth - logoWidth - 4, // 4pt padding from page edge
      4, // 4pt from top edge
      logoWidth,
      logoHeight
    );
    logoLoaded = true;
  } catch (error) { console.error('Error loading logo:', error); }

  // Calculate where the border should start (just below the logo + padding)
  const borderTop = (logoLoaded ? logoHeight + 12 : borderMargin);
  const borderHeight = pageHeight - borderTop - borderMargin;

  // Draw background and border for the first page
  doc.setFillColor(bgColor);
  doc.roundedRect(
    borderMargin,
    borderTop,
    pageWidth - 2 * borderMargin,
    borderHeight,
    borderRadius,
    borderRadius,
    'F'
  );
  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.7);
  doc.roundedRect(
    borderMargin,
    borderTop,
    pageWidth - 2 * borderMargin,
    borderHeight,
    borderRadius,
    borderRadius,
    'S'
  );

  let yOffset = borderTop + 12;

  // Add main title below the logo, left-aligned with content
  doc.setFontSize(16);
  doc.setTextColor(89, 185, 193); // #59B9C1
  doc.text(
    'How well are you personalising your engagement with healthcare professionals?',
    contentMargin,
    yOffset + 5,
    { maxWidth: pageWidth - 2 * contentMargin }
  );
  yOffset += 15;

  // Section: Your Results
  doc.setFontSize(20);
  doc.setTextColor(89, 185, 193);
  doc.text('Your Benchmark Results', pageWidth / 2, yOffset + 10, { align: 'center' });
  yOffset += 15;

  // Add the chart image if available (downscale to 150x150 px in PDF)
  if (chartImageBase64) {
    const imgWidth = 150;
    const imgHeight = 150;
    const x = (pageWidth - imgWidth) / 2;
    const y = yOffset;

    // Add a grey background behind the chart to make the white text visible
    doc.setFillColor(209, 213, 219); // Medium grey (#d1d5db)
    doc.rect(x, y, imgWidth, imgHeight, 'F'); // Draw a filled rectangle

    doc.addImage(chartImageBase64, 'PNG', x, y, imgWidth, imgHeight, undefined, 'FAST');
    yOffset += imgHeight + 15;
  }

  // Your Overall Score (large, colored)
  doc.setFontSize(16);
  doc.setTextColor(89, 185, 193);
  doc.text('Your Overall Score', pageWidth / 2, yOffset, { align: 'center' });
  yOffset += 15;
  doc.setFontSize(32);
  doc.text(`${overallRating}%`, pageWidth / 2, yOffset, { align: 'center' });
  yOffset += 5;

  // Explanatory text
  doc.setFontSize(12);
  doc.setTextColor(100);
  doc.text('How to increase your score to 100%?\nPlease refer to your analysis details below or contact us.', pageWidth / 2, yOffset + 8, { align: 'center', maxWidth: pageWidth - 2 * contentMargin });
  yOffset += 5;

  // Add a page if needed
  if (yOffset > pageHeight - borderMargin - 100) {
    doc.addPage();
    // Draw background and border for the new page
    doc.setFillColor(bgColor);
    doc.roundedRect(
      borderMargin,
      borderMargin,
      pageWidth - 2 * borderMargin,
      pageHeight - 2 * borderMargin,
      borderRadius,
      borderRadius,
      'F'
    );
    doc.setDrawColor(borderColor);
    doc.setLineWidth(0.7);
    doc.roundedRect(
      borderMargin,
      borderMargin,
      pageWidth - 2 * borderMargin,
      pageHeight - 2 * borderMargin,
      borderRadius,
      borderRadius,
      'S'
    );
    yOffset = borderMargin + 12;
  }

  // Key Strengths
  doc.setFontSize(16);
  doc.setTextColor(89, 185, 193);
  doc.text('Key Strengths', contentMargin, yOffset);
  yOffset += 10;
  doc.setFontSize(12);
  doc.setTextColor(51, 51, 51); // Dark grey for readability
  if (strengths.length > 0) {
    strengths.forEach((item) => {
      if (yOffset > pageHeight - borderMargin - 20) { doc.addPage(); yOffset = borderMargin + 12; }
      const dimensionName = Array.isArray(item.dimension) ? item.dimension.join(' ') : item.dimension;
      doc.text(`• ${dimensionName}: ${item.score}% (Industry Avg: ${item.avg}%)`, contentMargin + 5, yOffset);
      yOffset += 8;
    });
  } else {
    doc.text('No specific strengths identified compared to industry average.', contentMargin + 5, yOffset);
    yOffset += 8;
  }
  yOffset += 15;

  // Areas for Improvement
  if (yOffset > pageHeight - borderMargin - 100) { doc.addPage(); yOffset = borderMargin + 12; }
  doc.setFontSize(16);
  doc.setTextColor(248, 86, 64);
  doc.text('Areas for Improvement', contentMargin, yOffset);
  yOffset += 10;
  doc.setFontSize(12);
  doc.setTextColor(51, 51, 51); // Dark grey for readability
  if (improvements.length > 0) {
    improvements.forEach((item) => {
      if (yOffset > pageHeight - borderMargin - 20) { doc.addPage(); yOffset = borderMargin + 12; }
      const dimensionName = Array.isArray(item.dimension) ? item.dimension.join(' ') : item.dimension;
      doc.text(`• ${dimensionName}: ${item.score}% (Industry Avg: ${item.avg}%)`, contentMargin + 5, yOffset);
      yOffset += 8;
    });
  } else {
    doc.text('Excellent results! All areas are above the industry average.', contentMargin + 5, yOffset);
    yOffset += 8;
  }

  // Footer note
  if (yOffset > pageHeight - borderMargin - 40) { doc.addPage(); yOffset = borderMargin + 12; }
  yOffset += 20;
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(
    'Note: This report compares your scores against the industry average. Leading pharmaceutical companies often aim towards best practice (100% in all areas) to maximize the benefits of HCP personalisation.',
    contentMargin,
    yOffset,
    { maxWidth: pageWidth - 2 * contentMargin }
  );
  yOffset += 18;

  // Assessment date at the very bottom, above copyright
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(
    `Assessment Date: ${new Date().toLocaleDateString()}`,
    contentMargin,
    pageHeight - borderMargin - 18
  );

  // Copyright
  doc.setFontSize(8);
  doc.setTextColor(100);
  doc.text('© Data Jukebox. All rights reserved.', pageWidth / 2, yOffset, { align: 'center' });

  if (returnBase64) {
    return doc.output('datauristring').split(',')[1];
  } else {
    doc.save('benchmark-assessment-report.pdf');
  }
} 